import { usePartyMutation } from "Api/Mutations/Party/usePartyMutation";
import { useSetStardustParticipationMutation } from "Api/Mutations/Loyalty/useSetStardustParticipationMutation";
import { useSetThanksToYouParticipationMutation } from "Api/Mutations/Lead/useSetThanksToYouParticipationMutation";
import { ThanksToYouParticipationCheckboxLabel } from "Components/Affiliate/ThanksToYou/ThanksToYouParticipationCheckboxLabel";
import { StardustParticipationCheckboxLabel } from "Components/Loyalty/Stardust/StardustParticipationCheckboxLabel";
import { PersonalInformation } from "Components/Onboarding/shared/PersonalInformation/PersonalInformation";
import type { PersonalInformationConfirmationFormModel } from "Components/Onboarding/shared/PersonalInformation/schema";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import { useCurrentUserParty } from "Hooks/useCurrentUserParty";
import { useThanksToYouParticipation } from "Hooks/useThanksToYouParticipation";
import {
  type PartyDto,
  ProgramParticipationSource,
  ProgramParticipationState,
} from "Infrastructure/Api/Api";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

const RESOURCES = Resources.Onboarding.Steps.PersonalInformation;

type Props = {
  onNextStepRequested: () => void;
};

export const PersonalInformationStep = ({ onNextStepRequested }: Props) => {
  const { t } = useResource();
  const { data: partyData, isFetching } = useCurrentUserParty();
  const { mutate: updateParty } = usePartyMutation({
    onError: () => toast.error(t(RESOURCES.SaveError)),
  });

  // ZS-1624: the Stardust consent is a field of the personal-information form, pre-checked by
  // product decision (the person declines by unticking), and is sent when the step is submitted,
  // like the party data, so it counts even if the rest of the wizard is never finished. Only in the
  // Customer context: the Stardust right belongs to the Customer role and OnboardingRoute admits a
  // Lite person in any context — elsewhere the consent dialog on the Stardust page asks instead.
  const isCustomerContext = useCurrentUserRole() === AccessRoleCodes.Customer;
  const { mutateAsync: setStardustParticipation } =
    useSetStardustParticipationMutation({
      onError: () => toast.error(t(Resources.Common.SaveError)),
    });
  // ZS-1623: the Díky Tobě consent, the same way — pre-checked, sent with the step, Customer
  // context only (the opt-out belongs to the Customer role; hunters and merchants are B2B). A
  // refusal cuts the referral made at sign-up, a consent releases the inviter's referral points.
  // Offered only while the person never answered (NotAsked): whoever refused joins from the program
  // section, a consent is not asked twice, and a person who left for good cannot answer again (the
  // wizard can be re-entered, e.g. when a Lite account completes the full registration later). The
  // form takes its defaults once, so the step waits for user-info before rendering it — otherwise
  // the checkbox could appear later, unchecked, and no answer would be sent. A failed user-info only
  // hides the checkbox (no state → not NotAsked): the registration must not stop over it, and a
  // person who is not asked stays in the program, which is the default.
  const { state: thanksToYouState, isLoading: isThanksToYouLoading } =
    useThanksToYouParticipation();
  const { mutateAsync: setThanksToYouParticipation } =
    useSetThanksToYouParticipationMutation({
      onError: () => toast.error(t(Resources.Common.SaveError)),
    });

  if (isFetching || isThanksToYouLoading) {
    return <CenteredCircularProgress />;
  }

  if (!partyData) {
    return <ErrorText>{t(RESOURCES.LoadingError)}</ErrorText>;
  }

  const isThanksToYouConsentVisible =
    isCustomerContext &&
    thanksToYouState === ProgramParticipationState.NotAsked;

  const onSave = (values: PartyDto) => {
    if (values.publicID) {
      updateParty({
        data: values,
        publicID: values.publicID,
      });
    }
  };

  // The consents are part of the step: the wizard moves on only once they are saved. A failed save
  // keeps the person on the step (the hooks already showed the toast) so that a refusal cannot be
  // lost silently — the person would stay in the program they just unticked.
  const onNextStep = async ({
    isStardustParticipant,
    isThanksToYouParticipant,
  }: PersonalInformationConfirmationFormModel) => {
    try {
      if (isStardustParticipant !== undefined) {
        await setStardustParticipation({
          state: isStardustParticipant
            ? ProgramParticipationState.OptedIn
            : ProgramParticipationState.OptedOut,
          source: ProgramParticipationSource.Registration,
        });
      }
      if (isThanksToYouParticipant !== undefined) {
        await setThanksToYouParticipation({
          state: isThanksToYouParticipant
            ? ProgramParticipationState.OptedIn
            : ProgramParticipationState.OptedOut,
        });
      }
    } catch {
      return;
    }
    onNextStepRequested();
  };

  return (
    <PersonalInformation
      onSave={onSave}
      initialValues={partyData}
      onNextStepRequested={onNextStep}
      stardustConsentLabel={
        isCustomerContext ? <StardustParticipationCheckboxLabel /> : undefined
      }
      thanksToYouConsentLabel={
        isThanksToYouConsentVisible ? (
          <ThanksToYouParticipationCheckboxLabel />
        ) : undefined
      }
    />
  );
};
